/* eslint-disable react/react-in-jsx-scope */
// TYPES FOR FILTERING : "string","number","select","boolean", "none"
import { Dropdown, ProgressBar } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import type { ICellRendererParams } from 'ag-grid-community';
import type { ReactNode } from 'react';
import { formatMinutes, getColor } from '@/helpers/utils';
import StatusCodeRenderer from '@/app/common/components/CellRenderers/StatusCodeRenderer';
import BadgeRenderer from '@/app/common/components/CellRenderers/BadgeRenderer';
import ArrayRenderer from '@/app/common/components/CellRenderers/ArrayRenderer';
import UriIssueRenderer from '@/app/common/components/CellRenderers/UriIssueRenderer';
import HtmlParser from '@/app/common/components/CellRenderers/HtmlParser';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import ProtocolRenderer from '@/app/common/components/CellRenderers/ProtocolRenderer';
import CanonicalStatsTextRenderer from '@/app/common/components/CellRenderers/CanonicalStatsTextRenderer';
import timeStampToDate from '@/helpers/timeStampToDate';
import {
  domain_linkColumn,
  http_links_on_httpsColumn,
  link_3xx_destinationColumn,
  link_alt_imgColumn,
  link_hrefColumn,
  link_href_fragmentColumn,
  link_href_protocolColumn,
  link_href_queryColumn,
  link_href_query_countColumn,
  link_href_typeColumn,
  link_is_alternateColumn,
  link_is_authorColumn,
  link_is_bookmarkColumn,
  link_is_externalColumn,
  link_is_imgColumn,
  link_is_licenseColumn,
  link_is_nofollowColumn,
  link_is_noopenerColumn,
  link_is_noreferrerColumn,
  link_is_publisherColumn,
  link_is_sponsoredColumn,
  link_is_tagColumn,
  link_is_ugcColumn,
  link_src_imgColumn,
  link_textColumn,
  link_text_badColumn,
  protocolColumn,
  ps_urlColumn,
  ps_url_depthColumn,
  urlColumn,
  content_typeColumn,
  langColumn
} from './columns';

import CountRenderer from '@/app/common/components/CellRenderers/CountRenderer';
import CrawlStatusRenderer from '@/app/common/components/CellRenderers/CrawlStatusRenderer';
import NumberRenderer from '@/app/common/components/CellRenderers/NumberRenderer';
import FalconLink from '@/components/common/FalconLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import healthScoreColorAndLabel from '@/helpers/healthScoreColorAndLabel';
import URLRendererWithLink from '@/app/common/components/CellRenderers/URLRendererWithLink';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import moment from 'jalali-moment';
import { AiTwotoneAlert } from 'react-icons/ai';
import {
  useStartCrawlingMutation,
  useStopCrawlingMutation
} from '@/app/Crawl/api/mainCrawlApi';
import { errorMessage, warningMessage } from '@/utils/toast';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { NewCrawlContext, RemoveCrawlContext } from '@/context/Context';
import { useContext, useState } from 'react';
import CancelModal from '@/app/common/components/CancelModal';
import useDomain from '@/hooks/useDomain';
import CrawlStatusChecker from '@/app/Crawl/components/common/CrawlStatusChecker';
import CrawlStatusError from '@/app/Crawl/components/common/CrawlStatusError';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';

type CrawlRow = Record<string, any>;
type CrawlCellParams = Omit<ICellRendererParams<CrawlRow, any>, 'data'> & {
  data: CrawlRow;
};
type CrawlColumn = {
  [key: string]: unknown;
  cellRenderer?: (params: CrawlCellParams) => ReactNode;
};

export const InternalLinkColumns: CrawlColumn[] = [
  ps_urlColumn({
    type: 'internal',
    details: 'internal',
    activeTab: 'overview'
  }),
  ps_url_depthColumn,
  protocolColumn,
  domain_linkColumn,
  link_hrefColumn({
    type: 'internal',
    details: 'internal',
    activeTab: 'overview'
  }),
  link_textColumn,
  link_text_badColumn,
  link_href_typeColumn,
  link_3xx_destinationColumn,
  link_href_protocolColumn,
  http_links_on_httpsColumn,
  link_href_fragmentColumn,
  link_href_queryColumn,
  link_href_query_countColumn,
  link_is_imgColumn,
  link_alt_imgColumn,
  link_src_imgColumn({
    // issues: 'images_without_alt',
    details: 'assets_all',
    activeTab: 'assets_all',
    type: 'image'
  }),
  link_is_nofollowColumn,
  link_is_noopenerColumn,
  link_is_noreferrerColumn,
  link_is_ugcColumn,
  link_is_sponsoredColumn,
  link_is_externalColumn,
  link_is_alternateColumn,
  link_is_tagColumn,
  link_is_publisherColumn,
  link_is_licenseColumn,
  link_is_authorColumn,
  link_is_bookmarkColumn
];

export const ExternalLinkColumns = (
  isMdUp: boolean | undefined
): CrawlColumn[] => [
  {
    ...ps_urlColumn({
      type: 'internal',
      details: 'internal',
      activeTab: 'overview'
    }),
    pinned: isMdUp ? 'right' : null
  },
  ps_url_depthColumn,
  protocolColumn,
  domain_linkColumn,
  link_hrefColumn({
    type: 'external',
    details: 'not-internal',
    activeTab: 'overview'
  }),
  link_textColumn,
  link_text_badColumn,
  link_href_typeColumn,
  link_3xx_destinationColumn,
  link_href_protocolColumn,
  http_links_on_httpsColumn,
  link_href_fragmentColumn,
  link_href_queryColumn,
  link_href_query_countColumn,
  link_is_imgColumn,
  link_alt_imgColumn,
  link_src_imgColumn({
    // issues: 'images_without_alt',
    details: 'assets_all',
    activeTab: 'assets_all',
    type: 'image'
  }),
  link_is_nofollowColumn,
  link_is_noopenerColumn,
  link_is_noreferrerColumn,
  link_is_ugcColumn,
  link_is_sponsoredColumn,
  link_is_externalColumn,
  link_is_alternateColumn,
  link_is_tagColumn,
  link_is_publisherColumn,
  link_is_licenseColumn,
  link_is_authorColumn,
  link_is_bookmarkColumn
];
const areDataExpired = (time: string | number | null | undefined): boolean => {
  const m = moment(Number(time) * 1000);
  const formattedDate = m.locale('fa');
  const currentJalaliTime = moment().locale('fa');
  return formattedDate.isBefore(currentJalaliTime);
};
export const HistoryColumns = (
  domain_slug: string | null | undefined,
  lastCrawlStatus: number | null | undefined
): CrawlColumn[] => [
  {
    searchable: false,
    field: 'start_url',
    minWidth: 150,
    cellRenderer: params => {
      const { data } = params;
      if (!data) return null;

      return (
        <ChildrensTooltip
          placement="top"
          showOnHover={false}
          delayToRemove={100}
          html={
            areDataExpired(data.data_retention)
              ? crawlTooltip.expiredCrawl
              : ''
          }
        >
          <URLRendererWithLink
            disabled={areDataExpired(data.data_retention)}
            to={`/crawl/overview/${data.job_page}/${domain_slug}/${data.host_name}`}
            url={data.start_url}
          />
        </ChildrensTooltip>
      );
    },
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    type: 'string',
    title: 'نشانی خزش',
    tooltip: crawlTooltip.columns.start_url
  },
  {
    searchable: true,
    field: 'health_score',
    type: 'number',
    title: 'سلامت سایت',
    tooltip: <p>{crawlTooltip.columns.health_score}</p>,
    width: 165,
    cellRenderer: params => {
      const { data } = params;
      if (!data) return null;

      return (
        <div className="d-flex gap-1 align-items-center">
          <span>{data.health_score}%</span>
          <span className="w-100">
            <ProgressBar
              style={{ height: 5 }}
              className="rounded-pill align-middle"
              variant={healthScoreColorAndLabel(params.value).prClass}
              now={params.value}
            />
            <style>
              {`.bg-health-score-success {
              background-color:${healthScoreColorAndLabel(95).color} !important;
            }`}
              {`.bg-health-score-yellow {
              background-color:${healthScoreColorAndLabel(85).color} !important;
            }`}
              {`.bg-health-score-warning {
              background-color:${healthScoreColorAndLabel(75).color} !important;
            }`}
              {`.bg-health-score-danger {
              background-color:${healthScoreColorAndLabel(55).color} !important;
            }`}
            </style>
          </span>
        </div>
      );
    }
  },
  {
    searchable: true,
    field: 'count_error',
    type: 'number',
    title: ' خطا',
    icon: <AiTwotoneAlert size={16} color={getColor('danger')} />,
    tooltip: crawlTooltip.columns.count_error,
    width: 150,
    cellRenderer: params => (
      <div className="d-flex h-100 align-items-center justify-content-around">
        <h6
          className={classNames('fs--2 mb-0', {
            'text-danger': params.data.change_count_error > 0,
            'text-success': params.data.change_count_error < 0
          })}
        >
          {params.data.change_count_error < 0
            ? -1 * params.data.change_count_error
            : params.data.change_count_error}
          {params.data.change_count_error > 0 ? (
            <FontAwesomeIcon icon="caret-up" className="ms-1" />
          ) : params.data.change_count_error < 0 ? (
            <FontAwesomeIcon icon="caret-down" className="ms-1" />
          ) : (
            ''
          )}
        </h6>
        <h6 className="mb-0">
          <FalconLink
            icon=""
            title={
              <div
                style={{
                  color: areDataExpired(params.data.data_retention)
                    ? ''
                    : getColor('navbar-vertical-default-link-active-color')
                }}
              >
                <NumberRenderer text={params.value} />
              </div>
            }
            to={`/crawl/check-list/${params.data.job_page}/${domain_slug}/${params.data.host_name}
              `}
            className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
          />
        </h6>
      </div>
    )
  },
  {
    searchable: true,
    field: 'count_warning',
    type: 'number',
    title: 'هشدار',
    icon: <AiTwotoneAlert size={16} color={getColor('warning')} />,
    tooltip: crawlTooltip.columns.count_warning,
    width: 150,
    cellRenderer: params => (
      <div className="d-flex h-100 align-items-center justify-content-around">
        <h6
          className={classNames('fs--2 mb-0', {
            'text-danger': params.data.change_count_warning > 0,
            'text-success': params.data.change_count_warning < 0
          })}
        >
          {params.data.change_count_warning < 0
            ? -1 * params.data.change_count_warning
            : params.data.change_count_warning}
          {params.data.change_count_warning > 0 ? (
            <FontAwesomeIcon icon="caret-up" className="ms-1" />
          ) : params.data.change_count_warning < 0 ? (
            <FontAwesomeIcon icon="caret-down" className="ms-1" />
          ) : (
            ''
          )}
        </h6>
        <h6 className="mb-0">
          <FalconLink
            icon=""
            title={
              <div
                style={{
                  color: areDataExpired(params.data.data_retention)
                    ? ''
                    : getColor('navbar-vertical-default-link-active-color')
                }}
              >
                <NumberRenderer text={params.value} />
              </div>
            }
            to={`/crawl/check-list/${params.data.job_page}/${domain_slug}/${params.data.host_name}
              `}
            className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
          />
        </h6>
      </div>
    )
  },

  {
    searchable: true,
    field: 'count_info',
    type: 'number',
    title: 'اطلاعیه',
    icon: <AiTwotoneAlert size={16} color={getColor('info')} />,
    tooltip: crawlTooltip.columns.count_info,
    width: 150,
    cellRenderer: params => (
      <div className="d-flex h-100 align-items-center justify-content-around">
        <h6
          className={classNames('fs--2 mb-0', {
            'text-danger': params.data.change_count_info > 0,
            'text-success': params.data.change_count_info < 0
          })}
        >
          {params.data.change_count_info < 0
            ? -1 * params.data.change_count_info
            : params.data.change_count_info}
          {params.data.change_count_info > 0 ? (
            <FontAwesomeIcon icon="caret-up" className="ms-1" />
          ) : params.data.change_count_info < 0 ? (
            <FontAwesomeIcon icon="caret-down" className="ms-1" />
          ) : (
            ''
          )}
        </h6>
        <h6 className="mb-0">
          <FalconLink
            icon=""
            title={
              <div
                style={{
                  color: areDataExpired(params.data.data_retention)
                    ? ''
                    : getColor('navbar-vertical-default-link-active-color')
                }}
              >
                <NumberRenderer text={params.value} />
              </div>
            }
            to={`/crawl/check-list/${params.data.job_page}/${domain_slug}/${params.data.host_name}
              `}
            className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
          />
        </h6>
      </div>
    )
  },
  {
    searchable: true,
    field: 'crawl_status',
    type: 'string',
    title: 'وضعیت خزش',
    tooltip: crawlTooltip.columns.crawl_status,
    width: 175,
    cellRenderer: params => (
      <CrawlStatusRenderer
        status={areDataExpired(params.data.data_retention) ? 13 : params.value}
      />
    )
  },
  {
    searchable: true,
    field: 'start_crawl_time_timestamp',
    type: 'string',
    title: 'تاریخ',
    tooltip: crawlTooltip.columns.start_crawl_time_timestamp,
    width: 150,
    cellRenderer: params => (
      <>{timeStampToDate(params.data.start_crawl_time_timestamp)}</>
    )
  },
  {
    searchable: true,
    field: 'crawl_duration',
    type: 'string',
    width: 120,
    title: 'مدت خزش',
    tooltip: crawlTooltip.columns.crawl_duration,
    cellRenderer: params => (
      <div className="w-100 text-center">{formatMinutes(params.value)}</div>
    )
  },

  {
    searchable: false,
    field: 'count_url_total',
    type: 'number',
    width: 150,
    title: 'تعداد آدرس',
    tooltip: crawlTooltip.columns.count_url_total
  },
  {
    searchable: true,
    field: 'count_page_total',
    type: 'number',
    title: 'صفحات',
    tooltip: crawlTooltip.columns.count_pages_total,
    width: 130,
    cellRenderer: params => (
      <FalconLink
        icon=""
        title={
          <div
            style={{
              color: areDataExpired(params.data.data_retention)
                ? ''
                : getColor('navbar-vertical-default-link-active-color')
            }}
          >
            <NumberRenderer text={params.value} />
          </div>
        }
        to={`/crawl/pages/${params.data.job_page}/${domain_slug}/${params.data.host_name}
        `}
        className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
      />
    )
  },
  {
    searchable: true,
    field: 'count_page_links_internal_total',
    type: 'number',
    title: 'پیوند داخلی',
    tooltip: crawlTooltip.columns.count_page_links_internal,
    width: 130,
    cellRenderer: params => (
      <FalconLink
        icon=""
        title={
          <div
            style={{
              color: areDataExpired(params.data.data_retention)
                ? ''
                : getColor('navbar-vertical-default-link-active-color')
            }}
          >
            <NumberRenderer text={params.value} />
          </div>
        }
        to={`/crawl/internal-link/${params.data.job_page}/${domain_slug}/${params.data.host_name}
        `}
        className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
      />
    )
  },
  {
    searchable: true,
    field: 'count_page_links_external_total',
    width: 130,
    type: 'number',
    title: 'پیوند خارجی',
    tooltip: crawlTooltip.columns.count_page_links_external,
    cellRenderer: params => (
      <FalconLink
        icon=""
        title={
          <div
            style={{
              color: areDataExpired(params.data.data_retention)
                ? ''
                : getColor('navbar-vertical-default-link-active-color')
            }}
          >
            <NumberRenderer text={params.value} />
          </div>
        }
        to={`/crawl/external-link/${params.data.job_page}/${domain_slug}/${params.data.host_name}
      `}
        className={`px-0 fw-medium w-100 ${areDataExpired(params.data.data_retention) && 'disabled-link'}`}
      />
    )
  },
  {
    searchable: true,
    field: 'count_resource_total',
    type: 'number',
    width: 120,
    title: 'تعداد منابع',
    tooltip: crawlTooltip.columns.count_resource_total
  },
  {
    searchable: true,
    field: 'data_retention',
    type: 'string',
    title: 'زمان حذف داده',
    tooltip: crawlTooltip.columns.data_retention,
    width: 150,
    cellRenderer: params => <>{timeStampToDate(params.data.data_retention)}</>
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: params => {
      const [startCrawling] = useStartCrawlingMutation();
      const [stopCrawling] = useStopCrawlingMutation();
      const [stopModalOpen, setStopModalOpen] = useState(false);
      const { domain } = useDomain();
      const { data: userInfo } = useGetUserInfoQuery();
      const {
        config,
        isSubmitted,
        setConfig,
        setIsSubmitted,
        setJobPage,
        jobPage,
        canceled,
        setCanceled
      } = useContext(NewCrawlContext);
      const {
        setJobPage: setRemJobPage,
        setStart_url: setRemStartUrl,
        setShow
      } = useContext(RemoveCrawlContext);
      const isCrawling = params.data.crawl_status === 5;
      const onStop = async (): Promise<void> => {
        setCanceled(true);
        setJobPage(params.data.job_page);
        await stopCrawling({
          job_page: params.data.job_page,
          domain_slug
        })
          .unwrap()
          .catch(() => {
            errorMessage('در توقف خزش خطایی رخ داده است');
            setCanceled(false);
          });
      };
      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
              <Dropdown.Item
                disabled={areDataExpired(params.data.data_retention)}
                as={Link}
                to={`/crawl/overview/${params.data.job_page}/${domain_slug}/${params.data.host_name}`}
                className={classNames(
                  'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1',
                  {
                    'text-decoration-line-through': areDataExpired(
                      params.data.data_retention
                    )
                  }
                )}
              >
                <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                مشاهده
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  if (!isSubmitted && lastCrawlStatus !== 5) {
                    setIsSubmitted(true);
                    setCanceled(false);
                    window.scrollTo({
                      top: document.documentElement.scrollHeight,
                      behavior: 'smooth'
                    });

                    startCrawling({
                      start_url: params.data.start_url,
                      domain_slug,
                      ...config
                    })
                      .unwrap()
                      .then(response => {
                        if (response.code !== 200) {
                          warningMessage(response.data);
                          setIsSubmitted(false);
                        } else if (
                          typeof response.data === 'object' &&
                          response.data !== null
                        ) {
                          setJobPage(response.data.job_page);
                        }
                      })
                      .catch(err => {
                        console.log(err);
                      });
                  } else
                    warningMessage(
                      'در حال خزش هستی. لطفا صبر کن تا خزش تمام بشه یا ابتدا خزش رو متوقف کن'
                    );
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="undo-alt" className="me-1 ms-2" />
                خزش مجدد
              </Dropdown.Item>
              {isCrawling && (
                <Dropdown.Item
                  disabled={canceled}
                  onClick={() => setStopModalOpen(true)}
                  className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                >
                  <FontAwesomeIcon icon="stop-circle" className="me-1 ms-2" />
                  توقف
                </Dropdown.Item>
              )}
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item
                onClick={() => {
                  if (params.data.crawl_status === 5) {
                    warningMessage(
                      'لطفا ابتدا خزش رو متوقف کن، سپس بعد از اتمام فرآیند خزش امکان حذف آن وجود داره.'
                    );
                  } else {
                    setRemJobPage(params.data.job_page);
                    setRemStartUrl(params.data.start_url);
                    setShow(true);
                  }
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <CancelModal
            setShow={setStopModalOpen}
            show={stopModalOpen}
            submitFunc={onStop}
            submitTitle="آره، متوقف بشه"
            title="توقف خزش"
            desc={`آیا مطمئن هستید میخواید خزش را برای دامنه ${domain} متوقف کنید؟`}
          />
        </span>
      );
    }
  }
];

//========================= Pages ======================//
export const PagesColumns = (
  crawl_status: number | null = null
): CrawlColumn[] => [
  urlColumn({ details: 'internal', type: 'internal', activeTab: 'overview' }),
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'status_code',
    type: 'number',
    searchable: true,
    tooltip: crawlTooltip.columns.status_code,
    width: 130,
    title: 'کد وضعیت',
    cellRenderer: params => {
      return (
        <StatusCodeRenderer
          statusCode={params.data.status_code}
          subtle
        />
      );
    }
  },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'is_4xx',
    type: 'boolean',
    searchable: true,
    width: 120,
    title: '4xx شده؟',
    tooltip: crawlTooltip.columns.is_4xx,
    cellRenderer: params => (
      <BadgeRenderer
        color={params.data.is_4xx ? getColor('warning') : getColor('gray-600')}
        textColor={getColor('white')}
        text={params.data.is_4xx ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'is_5xx',
    searchable: true,
    type: 'boolean',
    width: 120,
    title: '5xx شده؟',
    tooltip: crawlTooltip.columns.is_5xx,
    cellRenderer: params => (
      <BadgeRenderer
        color={params.data.is_5xx ? getColor('danger') : getColor('gray-600')}
        textColor={getColor('white')}
        text={params.data.is_5xx ? 'آره' : 'نه'}
      />
    )
  },
  // {
  //   gp: 'اطلاعات کلی از صفحه',
  //   field: 'count_info',
  //   type: 'number',
  //   width: 140,
  //   title: 'تعداد اطلاعیه',
  //   tooltip: (
  //     <p>
  // تعداد اطلاعیه هایی که برای یک نشانی از سایت شما در آخرین خزش پیدا شده. بهتر هست این موارد رو بررسی کنین. بعضی از این موارد اهمیت زیادی دارن و بعضی موارد کمتر
  //     </p>
  //   ),
  // },
  // {
  //   gp: 'اطلاعات کلی از صفحه',
  //   field: 'count_warning',
  //   type: 'number',
  //   width: 135,
  //   title: 'تعداد هشدار',
  //   tooltip: (
  //     <p>
  // تعداد هشدارهایی که برای یک صفحه از سایت شما در آخرین خزش پیدا شده. این موارد رو بررسی کنین چون قسمتی از سلامت سایت شما مربوط به این هشدار ها هست
  //     </p>
  //   ),
  // },
  // {
  //   gp: 'اطلاعات کلی از صفحه',
  //   field: 'count_error',
  //   type: 'number',
  //   width: 120,
  //   title: 'تعداد خطا',
  //   tooltip: (
  //     <p>
  // تعداد خطاهایی که برای یک صفحه از سایت شما در آخرین خزش پیدا شده. حتما این موارد رو بررسی کنین چون اهمیت بالایی دارن و قسمت زیادی از سلامت سایت شما مربوط به این خطا ها هست
  //     </p>
  //   ),
  // },
  // },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'uri_issue',
    type: 'array',
    width: 135,
    title: 'مسئله نشانی',
    tooltip: crawlTooltip.columns.uri_issue,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <UriIssueRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.uri_issue}
        />
      );
    }
  },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'response_time',
    type: 'number',
    width: 125,
    title: 'زمان پاسخ',
    searchable: true,
    tooltip: crawlTooltip.columns.response_time,
    cellRenderer: params => (
      <div>{Number(params.data?.response_time).toFixed(1)}</div>
    )
  },
  content_typeColumn,
  langColumn,
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'length_html',
    type: 'number',
    searchable: true,
    width: 100,
    title: 'اندازه',
    tooltip: crawlTooltip.columns.length_html
  },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'is_size_high',
    type: 'boolean',
    searchable: true,
    width: 147,
    title: 'سنگین هست؟',
    tooltip: crawlTooltip.columns.is_size_high,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.is_size_high ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.is_size_high ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'اطلاعات کلی از صفحه',
    field: 'header_key',
    type: 'array',
    width: 170,
    title: 'هدرهای درخواست',
    searchable: true,
    tooltip: crawlTooltip.columns.header_key,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.header_key}
        />
      );
    }
  },
  {
    searchable: true,
    gp: 'عنوان (meta title)',
    field: 'title',
    type: 'string',
    title: 'عنوان صفحه',
    tooltip: crawlTooltip.columns.title
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_not_using',
    type: 'boolean',
    width: 125,
    searchable: true,
    title: 'عنوان داره؟',
    tooltip: crawlTooltip.columns.title_not_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_not_using
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={!params.data.title_not_using ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_count',
    type: 'number',
    searchable: true,
    width: 130,
    title: 'تعداد عنوان',
    tooltip: crawlTooltip.columns.title_count
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_several_using',
    type: 'boolean',
    width: 150,
    searchable: true,
    title: 'چند عنوان داره؟',
    tooltip: crawlTooltip.columns.title_several_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_several_using
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_several_using ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_have_emoji',
    type: 'boolean',
    searchable: true,
    width: 185,
    title: 'ایموجی در عنوان داره؟',
    tooltip: crawlTooltip.columns.title_have_emojis,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_have_emoji
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_have_emoji ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    searchable: true,
    field: 'title_most_repeated_word',
    type: 'string',
    width: 202,
    title: 'کلمات پراستفاده در عنوان',
    tooltip: crawlTooltip.columns.title_most_repeated_word,
    cellRenderer: params => (
      <HtmlParser html={params.data.title_most_repeated_word} />
    )
  },
  {
    gp: 'عنوان (meta title)',
    searchable: true,
    field: 'title_not_using_in_text',
    type: 'string',
    width: 315,
    title: 'کلماتی از عنوان که داخل متن صفحه نبوده است',
    tooltip: crawlTooltip.columns.title_not_using_in_text,
    cellRenderer: params => (
      <HtmlParser html={params.data.title_not_using_in_text} />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_not_using_in_text_count',
    type: 'number',
    searchable: true,
    width: 310,
    title: 'تعداد کلمات عنوانی که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.title_not_using_in_text_count
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_have_archive',
    type: 'boolean',
    searchable: true,
    width: 150,
    title: 'عنوان آرشیو دار',
    tooltip: crawlTooltip.columns.title_have_archive,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_have_archive
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_have_archive ? 'داره' : 'نداره'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_char',
    type: 'number',
    searchable: true,
    width: 173,
    title: 'مقدار عنوان (کاراکتر)',
    tooltip: crawlTooltip.columns.title_char
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_px',
    type: 'number',
    searchable: true,
    width: 178,
    title: 'مقدار عنوان (پیکسل)',
    tooltip: crawlTooltip.columns.title_px
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_char_is_long',
    type: 'boolean',
    searchable: true,
    width: 225,
    title: 'عنوان طولانی هست؟ (کاراکتر)',
    tooltip: crawlTooltip.columns.title_char_is_long,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_char_is_long
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_char_is_long ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_px_is_long',
    type: 'boolean',
    searchable: true,
    width: 225,
    title: 'عنوان طولانی هست؟ (پیکسل)',
    tooltip: crawlTooltip.columns.title_px_is_long,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_px_is_long
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_px_is_long ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_char_is_short',
    type: 'boolean',
    width: 215,
    searchable: true,
    title: 'عنوان کوتاه هست؟ (کاراکتر)',
    tooltip: crawlTooltip.columns.title_char_is_short,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_char_is_short
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_char_is_short ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'عنوان (meta title)',
    field: 'title_px_is_short',
    type: 'boolean',
    searchable: true,
    width: 217,
    title: 'عنوان کوتاه هست؟ (پیکسل)',
    tooltip: crawlTooltip.columns.title_px_is_short,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.title_px_is_short
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.title_px_is_short ? 'آره' : 'نه'}
      />
    )
  },
  // {
  //   gp: 'عنوان (meta title)',
  //   field: 'title_is_duplicate',
  //   type: 'boolean',
  //   title: 'عنوان تکراری هست؟',
  //   cellRenderer: params => (
  //     <BadgeRenderer
  //       color={
  //         params.data.title_is_duplicate
  //           ? getColor('danger')
  //           : getColor('gray-600')
  //       }
  //       textColor={getColor('gray-500')}
  //       text={params.data.title_is_duplicate ? 'آره' : 'نه'}
  //     />
  //   )
  // },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    searchable: true,
    field: 'meta_desc',
    type: 'string',
    title: 'توضیح متا',
    tooltip: crawlTooltip.columns.meta_desc
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_not_using',
    type: 'boolean',
    searchable: true,
    width: 152,
    title: 'توضیح متا داره؟',
    tooltip: crawlTooltip.columns.meta_desc_not_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.meta_desc_not_using
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.meta_desc_not_using ? 'نداره' : 'داره'}
      />
    )
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_char',
    type: 'number',
    searchable: true,
    width: 200,
    title: 'مقدار توضیح متا (کاراکتر)',
    tooltip: crawlTooltip.columns.meta_desc_char
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_count',
    type: 'number',
    searchable: true,
    width: 155,
    title: 'تعداد توضیح متا',
    tooltip: crawlTooltip.columns.meta_desc_counts
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_px',
    type: 'number',
    searchable: true,
    width: 202,
    title: 'مقدار توضیح متا (پیکسل)',
    tooltip: crawlTooltip.columns.meta_desc_px
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_char_is_long',
    type: 'boolean',
    searchable: true,
    width: 250,
    title: 'توضیح متا طولانی هست؟ (کاراکتر)',
    tooltip: crawlTooltip.columns.meta_desc_char_is_long,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.meta_desc_char_is_long
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.meta_desc_char_is_long ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_px_is_long',
    type: 'boolean',
    searchable: true,
    width: 250,
    title: 'توضیح متا طولانی هست؟ (پیکسل)',
    tooltip: crawlTooltip.columns.meta_desc_px_is_long,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.meta_desc_px_is_long
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.meta_desc_px_is_long ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'توضیح متا (متاتگ دسکریپشن)',
    field: 'meta_desc_char_is_short',
    type: 'boolean',
    searchable: true,
    width: 240,
    title: 'توضیح متا کوتاه هست؟ (کاراکتر)',
    tooltip: crawlTooltip.columns.meta_desc_char_is_short,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.meta_desc_char_is_short
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.meta_desc_char_is_short ? 'آره' : 'نه'}
      />
    )
  },
  // {
  //   field: 'meta_desc_is_duplicate',
  //   type: 'boolean',
  //   title: 'توضیح متا تکراری هست؟',
  //   cellRenderer: params => (
  //     <BadgeRenderer
  //       color={
  //         params.data.meta_desc_is_duplicate
  //           ? getColor('danger')
  //           : getColor('gray-600')
  //       }
  //       textColor={getColor('gray-500')}
  //       text={params.data.meta_desc_is_duplicate ? 'آره' : 'نه'}
  //     />
  //   )
  // },
  {
    gp: 'تگ H1',
    field: 'h1',
    type: 'array',
    title: 'H1',
    tooltip: crawlTooltip.columns.h1,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h1}
        />
      );
    }
  },
  {
    gp: 'تگ H1',
    field: 'h1_count',
    type: 'number',
    searchable: true,
    width: 110,
    title: 'تعداد H1',
    tooltip: crawlTooltip.columns.h1_count
  },
  {
    gp: 'تگ H1',
    field: 'h1_not_using_in_text',
    type: 'array',
    width: 260,
    title: 'کلمات H1 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h1_not_using_in_text,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h1_not_using_in_text}
        />
      );
    }
  },
  {
    gp: 'تگ H1',
    field: 'h1_not_using_in_text_count',
    type: 'number',
    width: 290,
    searchable: true,
    title: 'تعداد کلمات H1 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h1_not_using_in_text_count
  },
  {
    gp: 'تگ H1',
    field: 'h1_not_using',
    searchable: true,
    type: 'boolean',
    width: 110,
    title: 'H1 نداره',
    tooltip: crawlTooltip.columns.h1_not_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.h1_not_using ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.h1_not_using ? 'نداره' : 'داره'}
      />
    )
  },
  {
    gp: 'تگ H2',
    field: 'h2',
    type: 'array',
    searchable: true,
    title: 'H2',
    tooltip: crawlTooltip.columns.h1_not_using,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h2}
        />
      );
    }
  },
  {
    gp: 'تگ H2',
    field: 'h2_count',
    searchable: true,
    type: 'number',
    width: 115,
    title: 'تعداد H2',
    tooltip: crawlTooltip.columns.h2_count
  },
  {
    gp: 'تگ H2',
    field: 'h2_not_using_in_text',
    type: 'array',
    searchable: true,
    width: 260,
    title: 'کلمات H2 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h2_not_using_in_text,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h2_not_using_in_text}
        />
      );
    }
  },
  {
    gp: 'تگ H2',
    field: 'h2_not_using_in_text_count',
    type: 'number',
    searchable: true,
    width: 290,
    title: 'تعداد کلمات H2 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h2_not_using_in_text_count
  },
  {
    gp: 'تگ H2',
    field: 'h2_not_using',
    type: 'boolean',
    searchable: true,
    width: 110,
    title: 'H2 نداره',
    tooltip: crawlTooltip.columns.h2_not_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.h2_not_using ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.h2_not_using ? 'نداره' : 'داره'}
      />
    )
  },
  {
    gp: 'تگ H3',
    field: 'h3',
    type: 'array',
    searchable: true,
    title: 'H3',
    tooltip: crawlTooltip.columns.h3,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h3}
        />
      );
    }
  },
  {
    gp: 'تگ H3',
    field: 'h3_count',
    type: 'number',
    searchable: true,
    width: 115,
    title: 'تعداد H3',
    tooltip: <p>تعداد H3 هایی که در صفحه وجود داره نمایش میده</p>
  },
  {
    gp: 'تگ H3',
    field: 'h3_not_using_in_text',
    type: 'array',
    searchable: true,
    width: 265,
    title: 'کلمات H3 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h3_not_using_in_text,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'overview',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.h3_not_using_in_text}
        />
      );
    }
  },
  {
    gp: 'تگ H3',
    field: 'h3_not_using_in_text_count',
    type: 'number',
    searchable: true,
    width: 295,
    title: 'تعداد کلمات H3 که داخل متن صفحه نبوده',
    tooltip: crawlTooltip.columns.h3_not_using_in_text
  },
  {
    gp: 'تگ H3',
    field: 'h3_not_using',
    type: 'boolean',
    searchable: true,
    width: 113,
    title: 'H3 نداره',
    tooltip: crawlTooltip.columns.h3_not_using,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.h3_not_using ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.h3_not_using ? 'نداره' : 'داره'}
      />
    )
  },
  {
    gp: 'تگ Canonical',
    searchable: true,
    field: 'canonical_url',
    type: 'string',
    width: 180,
    title: 'نشانی کنونیکال',
    tooltip: crawlTooltip.columns.canonical_url,
    cellRenderer: params => <URLRenderer url={params.data.canonical_url} />
  },
  {
    gp: 'تگ Canonical',
    field: 'canonical_url_status_code',
    type: 'number',
    searchable: true,
    width: 190,
    title: 'وضعیت نشانی کنونیکال',
    tooltip: crawlTooltip.columns.canonical_url_status_code,
    cellRenderer: params => (
      <StatusCodeRenderer statusCode={params.data.canonical_url_status_code} />
    )
  },
  {
    gp: 'تگ Canonical',
    field: 'canonical_is_itself',
    type: 'number',
    searchable: true,
    width: 145,
    title: 'تگ کنونیکال',
    tooltip: crawlTooltip.columns.canonical_is_itself,
    cellRenderer: params => (
      <CanonicalStatsTextRenderer num={params.data.canonical_is_itself} />
    )
  },
  {
    gp: 'تگ Canonical',
    field: 'canonical_is_not_itself',
    type: 'boolean',
    searchable: true,
    width: 150,
    title: 'کنونیکال به غیر',
    tooltip: crawlTooltip.columns.canonical_is_not_itself,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.canonical_is_not_itself
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.canonical_is_not_itself ? 'هست' : 'نیست'}
      />
    )
  },
  {
    gp: 'تگ های مهم صفحه بندی، Pagination',
    searchable: true,
    field: 'rel_next',
    type: 'string',
    width: 180,
    title: 'مقدار rel=next',
    tooltip: crawlTooltip.columns.rel_next,
    cellRenderer: params => <URLRenderer url={params.data.rel_next} />
  },
  {
    gp: 'تگ های مهم صفحه بندی، Pagination',
    searchable: true,
    field: 'rel_prev',
    type: 'string',
    width: 180,
    title: 'مقدار rel=prev',
    tooltip: crawlTooltip.columns.rel_prev,
    cellRenderer: params => <URLRenderer url={params.data.rel_prev} />
  },
  {
    gp: 'متا تگ robots',
    field: 'robots_is_noindex',
    type: 'boolean',
    searchable: true,
    width: 163,
    tooltip: crawlTooltip.columns.robots_is_noindex,
    title: 'نوایندکس هست؟',
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.robots_is_noindex
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.robots_is_noindex ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'متا تگ robots',
    field: 'robots_is_nofollow',
    type: 'boolean',
    searchable: true,
    width: 140,
    tooltip: crawlTooltip.columns.robots_is_nofollow,
    title: 'نوفالو هست؟',
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.robots_is_nofollow
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.robots_is_nofollow ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'تغییر مسیر (Redirect)',
    field: 'is_3xx',
    searchable: true,
    type: 'boolean',
    width: 150,
    title: 'ریدایرکت شده؟',
    tooltip: crawlTooltip.columns.is_3xx,
    cellRenderer: params => (
      <BadgeRenderer
        color={params.data.is_3xx ? '#bb881d' : getColor('gray-600')}
        textColor={getColor('white')}
        text={params.data.is_3xx ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'تغییر مسیر (Redirect)',
    searchable: true,
    field: 'redirect_url',
    type: 'string',
    width: 180,
    title: 'نشانی ریدایرکت',
    tooltip: crawlTooltip.columns.redirect_url,
    cellRenderer: params => <URLRenderer url={params.data.redirect_url} />
  },
  {
    gp: 'تغییر مسیر (Redirect)',
    field: 'redirect_is_chain',
    type: 'boolean',
    searchable: true,
    width: 160,
    title: 'ریدایرکت پیوسته',
    tooltip: crawlTooltip.columns.redirect_is_chain,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.redirect_is_chain
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.redirect_is_chain ? 'داره' : 'نداره'}
      />
    )
  },
  {
    gp: 'تغییر مسیر (Redirect)',
    field: 'redirect_is_loop',
    type: 'boolean',
    searchable: true,
    width: 155,
    title: 'ریدایرکت حلقه‌ای',
    tooltip: crawlTooltip.columns.redirect_is_loop,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.redirect_is_loop
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.redirect_is_loop ? 'داره' : 'نداره'}
      />
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'inlink_count',
    type: 'number',
    searchable: true,
    width: 175,
    title: 'صفحه ارجاع',
    tooltip: crawlTooltip.columns.inlink_count,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CrawlStatusChecker crawl_status={crawl_status}>
          {params.value === -1 ? (
            <CrawlStatusError />
          ) : (
            <CountRenderer
              onClick={() => {
                setSearchParams({
                  details: 'internal',
                  activeTab: 'links',
                  page_url: params.data.url,
                  job_page: params.data.job_page,
                  status_code: params.data.status_code
                });
              }}
              data={params.data.inlink_count}
            />
          )}
        </CrawlStatusChecker>
      );
    }
  },
  {
    gp: 'پیوند (لینک)',
    field: 'unique_inlink_count',
    type: 'number',
    searchable: true,
    width: 207,
    title: 'صفحه ارجاع (یکتا)',
    tooltip: crawlTooltip.columns.unique_inlink_count,
    cellRenderer: params => (
      <CrawlStatusChecker crawl_status={crawl_status}>
        {params.value === -1 ? <CrawlStatusError /> : params.value}
      </CrawlStatusChecker>
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'is_low_unique_inlink',
    type: 'boolean',
    searchable: true,
    width: 250,
    title: 'پیوند داخلی کم شده؟ (صفحه تنها)',
    tooltip: crawlTooltip.columns.is_low_unique_inlink,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.is_low_unique_inlink
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.is_low_unique_inlink ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'outlink_internal_count',
    type: 'number',
    searchable: true,
    width: 175,
    title: 'پیوند داخلی',
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CrawlStatusChecker crawl_status={crawl_status}>
          {params.value === -1 ? (
            <CrawlStatusError />
          ) : (
            <CountRenderer
              onClick={() => {
                setSearchParams({
                  details: 'internal',
                  activeTab: 'outlink_internal',
                  page_url: params.data.url,
                  job_page: params.data.job_page,
                  status_code: params.data.status_code
                });
              }}
              data={params.data.outlink_internal_count}
            />
          )}
        </CrawlStatusChecker>
      );
    },
    tooltip: crawlTooltip.columns.outlink_internal_count
  },
  {
    gp: 'پیوند (لینک)',
    field: 'unique_internal_outlink_count',
    type: 'number',
    searchable: true,
    width: 207,
    title: 'پیوند داخلی (یکتا)',
    tooltip: crawlTooltip.columns.unique_internal_outlink_count,
    cellRenderer: params => (
      <CrawlStatusChecker crawl_status={crawl_status}>
        {params.value === -1 ? <CrawlStatusError /> : params.value}
      </CrawlStatusChecker>
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'outlink_external_count',
    type: 'number',
    searchable: true,
    width: 175,
    title: 'پیوند خارجی',
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CrawlStatusChecker crawl_status={crawl_status}>
          {params.value === -1 ? (
            <CrawlStatusError />
          ) : (
            <CountRenderer
              onClick={() => {
                setSearchParams({
                  details: 'internal',
                  activeTab: 'outlink_external',
                  page_url: params.data.url,
                  job_page: params.data.job_page,
                  status_code: params.data.status_code
                });
              }}
              data={params.data.outlink_external_count}
            />
          )}
        </CrawlStatusChecker>
      );
    },
    tooltip: crawlTooltip.columns.outlink_external_count
  },
  {
    gp: 'پیوند (لینک)',
    field: 'unique_external_outlink_count',
    type: 'number',
    searchable: true,
    width: 210,
    title: ' پیوند خارجی (یکتا)',
    tooltip: crawlTooltip.columns.unique_external_outlink_count,
    cellRenderer: params => (
      <CrawlStatusChecker crawl_status={crawl_status}>
        {params.value === -1 ? <CrawlStatusError /> : params.value}
      </CrawlStatusChecker>
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'is_page_high_unique_external_outline',
    type: 'boolean',
    width: 270,
    searchable: true,
    title: 'صفحه پیوند خارجی زیادی انجام داده؟',
    tooltip: crawlTooltip.columns.is_page_high_unique_external_outline,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.is_page_high_unique_external_outline
            ? getColor('danger')
            : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.is_page_high_unique_external_outline ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'پیوند (لینک)',
    field: 'depth',
    type: 'number',
    searchable: true,
    width: 115,
    title: 'عمق صفحه',
    tooltip: crawlTooltip.columns.depth
  },
  {
    gp: 'پیوند (لینک)',
    field: 'is_depth_high',
    type: 'boolean',
    searchable: true,
    width: 165,
    title: 'عمق صفحه زیاده؟',
    tooltip: crawlTooltip.columns.is_depth_high,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.is_depth_high ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.is_depth_high ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'چگالی صفحه (keyword stuffing)',
    field: 'density_words_1',
    type: 'array',
    width: 145,
    title: 'چگالی تک کلمه',
    tooltip: crawlTooltip.columns.density_words_1,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'density',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.density_words_1}
        />
      );
    }
  },
  {
    gp: 'چگالی صفحه (keyword stuffing)',
    field: 'density_words_2',
    type: 'array',
    width: 145,
    title: 'چگالی دو کلمه',
    tooltip: crawlTooltip.columns.density_words_2,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'density',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.density_words_1}
        />
      );
    }
  },
  {
    gp: 'چگالی صفحه (keyword stuffing)',
    field: 'density_words_3',
    type: 'array',
    title: 'چگالی سه کلمه',
    tooltip: crawlTooltip.columns.density_words_3,
    width: 145,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <ArrayRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'density',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.density_words_1}
        />
      );
    }
  },
  {
    gp: 'منابع صفحه (Resource Page)',
    searchable: true,
    field: 'image_count',
    type: 'string',
    width: 130,
    title: 'تعداد تصویر',
    tooltip: crawlTooltip.columns.image_count,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CountRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'image',
              type: 'image',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.image_count}
        />
      );
    }
  },
  {
    gp: 'منابع صفحه (Resource Page)',
    field: 'css_count',
    type: 'number',
    searchable: true,
    width: 125,
    title: 'تعداد CSS',
    tooltip: crawlTooltip.columns.css_count,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CountRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'css',
              type: 'css',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.css_count}
        />
      );
    }
  },
  {
    gp: 'منابع صفحه (Resource Page)',
    field: 'js_count',
    type: 'number',
    searchable: true,
    width: 115,
    title: 'تعداد JS',
    tooltip: crawlTooltip.columns.js_count,
    cellRenderer: params => {
      const [, setSearchParams] = useSearchParams();
      return (
        <CountRenderer
          onClick={() => {
            setSearchParams({
              details: 'internal',
              activeTab: 'js',
              page_url: params.data.url,
              job_page: params.data.job_page,
              status_code: params.data.status_code
            });
          }}
          data={params.data.js_count}
        />
      );
    }
  },

  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'low_word',
    type: 'boolean',
    searchable: true,
    width: 160,
    title: 'کم محتوا هست؟',
    tooltip: crawlTooltip.columns.low_word,
    cellRenderer: params => (
      <BadgeRenderer
        color={params.data.low_word ? getColor('danger') : getColor('gray-600')}
        textColor={getColor('white')}
        text={params.data.low_word ? 'آره' : 'نه'}
      />
    )
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'high_word',
    type: 'boolean',
    searchable: true,
    width: 213,
    title: 'محتوای بیش از اندازه داره؟',
    tooltip: crawlTooltip.columns.high_word,
    cellRenderer: params => (
      <BadgeRenderer
        color={
          params.data.high_word ? getColor('danger') : getColor('gray-600')
        }
        textColor={getColor('white')}
        text={params.data.high_word ? 'داره' : 'نداره'}
      />
    )
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'word_count',
    type: 'number',
    searchable: true,
    width: 170,
    title: 'تعداد کلمات صفحه',
    tooltip: crawlTooltip.columns.word_count
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'unique_words_count',
    type: 'number',
    searchable: true,
    width: 235,
    title: 'تعداد کلمات منحصربه‌فرد صفحه',
    tooltip: crawlTooltip.columns.unique_words_count
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'char_count',
    type: 'number',
    searchable: true,
    width: 170,
    title: 'تعداد کاراکتر صفحه',
    tooltip: crawlTooltip.columns.char_count
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'sentence_count',
    type: 'number',
    searchable: true,
    width: 140,
    title: 'تعداد جملات',
    tooltip: crawlTooltip.columns.sentence_count
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'reading_time',
    type: 'number',
    searchable: true,
    width: 160,
    title: 'مدت زمان مطالعه',
    tooltip: crawlTooltip.columns.reading_time
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    searchable: true,
    field: 'published_time',
    type: 'string',
    width: 130,
    title: 'تاریخ انتشار',
    tooltip: crawlTooltip.columns.published_time,
    cellRenderer: params => {
      const date = new Date(params?.data?.published_time)?.getTime();

      return <>{timeStampToDate(Math.floor(date / 1000))}</>;
    }
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    searchable: true,
    field: 'last_modified',
    type: 'string',
    width: 190,
    title: 'آخرین تاریخ ویرایش',
    tooltip: crawlTooltip.columns.last_modified,
    cellRenderer: params => {
      const date = new Date(params?.data?.published_time)?.getTime();

      return <>{timeStampToDate(Math.floor(date / 1000))}</>;
    }
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    searchable: true,
    field: 'last_modified_by_day',
    type: 'number',
    width: 207,
    title: 'چند روزه صفحه آپدیت نشده؟',
    tooltip: crawlTooltip.columns.last_modified_by_day
  },
  {
    gp: 'اطلاعات مربوط به محتوا (متن) صفحه',
    field: 'old_blog',
    type: 'boolean',
    searchable: true,
    width: 170,
    title: 'مقاله قدیمی شده؟',
    tooltip: crawlTooltip.columns.old_blog,
    cellRenderer: params => (
      <BadgeRenderer
        color={params.data.old_blog ? getColor('danger') : getColor('gray-600')}
        textColor={getColor('white')}
        text={params.data.old_blog ? 'آره' : 'نه'}
      />
    )
  },
  {
    field: 'type',
    gp: 'سایر اطلاعات',
    type: 'string',
    searchable: true,
    width: 136,
    title: 'نوع',
    tooltip: crawlTooltip.columns.type,
    cellRenderer: params => (
      <>{params.data.type === 'internal' ? 'صفحه داخلی' : 'صفحه خارجی'}</>
    )
  },
  {
    field: 'protocol',
    gp: 'سایر اطلاعات',
    type: 'string',
    searchable: true,
    width: 120,
    title: 'پروتکل',
    tooltip: crawlTooltip.columns.protocol,
    cellRenderer: params => <ProtocolRenderer protocol={params.data.protocol} />
  }
];
